import React from 'react'
import { sheetAtom } from 'atoms/atoms'
import { useSetAtom } from 'jotai'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Callout from '@node-space/storybook-components/dist/Callout'
import CheckBox from '@node-space/storybook-components/dist/CheckBox'
import {
  DateErrorType,
  DateOfBirth,
  DateType,
} from '@node-space/storybook-components/dist/DateOfBirth'
import { Input } from '@node-space/storybook-components/dist/Input'
import { Radio } from '@node-space/storybook-components/dist/Radio'
import Select from '@node-space/storybook-components/dist/Select'
import { Box, Button, Box as FormActions, Box as FormFields, Text } from '@node-space/ui-components'
import { useDateInput } from 'hooks/forms/useDateInput'
import { SheetName, SheetState } from 'types/types'
import { contactAtom } from './atoms/contactAtoms'
import { useContactForm } from './hooks/useContactForm'

export const ContactForm = () => {
  const { t } = useTranslation()
  const setSheet = useSetAtom(sheetAtom)
  const setContact = useSetAtom(contactAtom)

  const {
    dateLabels,
    hasDateErrors,
    getDateFields,
    setDateInputValue,
    setDateErrors,
    getDateErrors,
    getDateErrorText,
  } = useDateInput()

  const {
    formFields,
    isCountriesLoading,
    errors,
    control,
    isSubmitting,
    isPostingContact,
    isCompany,
    isIndividual,
    isEditingContact,
    isUpdatingContact,
    requestError,
    handleSubmit,
    onSubmit,
  } = useContactForm({ setDateErrors })

  const isSubmittingAddContact = isSubmitting || isPostingContact
  const isSubmittingUpdateContact = isSubmitting || isUpdatingContact

  const handleOnClickCancel = () => {
    setContact(null)
    setSheet({
      [SheetName.CONTACT]: SheetState.CLOSED,
    })
  }

  return (
    <>
      <form data-testid="contact-form" onSubmit={handleSubmit(onSubmit)} className="w-full">
        <FormFields width="full" flexCol gapY={16} paddingT={16}>
          {requestError?.show && (
            <Callout state="error" showCloseButton={false} testid="form-error-callout">
              <Text variant="caption-text" color="inherit">
                {requestError?.handledMessage}
              </Text>
            </Callout>
          )}
          <Radio testid="entity-type" {...formFields.entityType} horizontal borderless={false} />
          {isCompany && <Input testid="company-name" {...formFields.companyName} />}
          {isIndividual && (
            <Box flex gapX={16}>
              <Input testid="first-name" {...formFields.firstName} />
              <Input testid="last-name" {...formFields.lastName} />
            </Box>
          )}
          <Input testid="description" {...formFields.contactDescription} />
          {isCompany && (
            <>
              <Input testid="registration-number" {...formFields.companyRegistrationNumber} />
              <Select
                testid="registration-country"
                {...formFields.companyRegistrationCountry}
                loading={isCountriesLoading}
                isSearchable
              />
            </>
          )}
          {isIndividual && (
            <Select
              testid="country-of-residence"
              {...formFields.countryOfResidence}
              loading={isCountriesLoading}
              isSearchable
            />
          )}
          <Input testid="address-line-1" {...formFields.addressLine1} />
          <Input testid="address-line-2" {...formFields.addressLine2} />
          <Box flex gapX={16}>
            <Input testid="city" {...formFields.city} />
            <Input testid="post-code" {...formFields.postCode} />
          </Box>
          <Input testid="region" {...formFields.region} />
          {isCompany && (
            <Box>
              <Controller
                control={control}
                name="dateOfIncorporation"
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <DateOfBirth
                    date={getDateFields(value)}
                    labels={{
                      ...dateLabels,
                      errorMessage: getDateErrorText(error),
                      dateOfBirth: t('contacts.dateOfIncorporation'),
                    }}
                    errors={getDateErrors(error)}
                    onChange={(date: DateType) => onChange(setDateInputValue(date))}
                    onValidate={(errors: DateErrorType) => setDateErrors(errors)}
                  />
                )}
              />
            </Box>
          )}
          {isIndividual && (
            <Box>
              <Controller
                control={control}
                name="dateOfBirth"
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <DateOfBirth
                    date={getDateFields(value)}
                    labels={{
                      ...dateLabels,
                      errorMessage: getDateErrorText(error),
                    }}
                    errors={getDateErrors(error)}
                    onChange={(date: DateType) => onChange(setDateInputValue(date))}
                    onValidate={(errors: DateErrorType) => setDateErrors(errors)}
                  />
                )}
              />
            </Box>
          )}
          {isCompany && <CheckBox {...formFields.isSelfOwned} testid="is-self-owned" />}
          <FormActions flex justify="between">
            <Button testid="cancel" variant="secondary" onClick={handleOnClickCancel}>
              {t('cancel')}
            </Button>

            <Button
              testid={isEditingContact ? 'update-contact' : 'create-contact'}
              type="submit"
              variant="primary"
              loading={isEditingContact ? isSubmittingUpdateContact : isSubmittingAddContact}
              disabled={!!Object.keys(errors)?.length || hasDateErrors}
            >
              {isEditingContact ? t('contacts.updateContact') : t('contacts.createContact')}
            </Button>
          </FormActions>
        </FormFields>
      </form>
    </>
  )
}
